"use client"

import dynamic from "next/dynamic"
import { useEffect, useRef } from "react"
import { useFeatureFlagVariantKey } from "posthog-js/react"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { trackEvent } from "@/lib/analytics"

const NewsletterSignupModal = dynamic(
  () => import("@/components/ui/newsletter-signup-modal").then((m) => m.NewsletterSignupModal),
  { ssr: false, loading: () => null }
)

const ExitScanModal = dynamic(
  () => import("@/components/blog/exit-scan-modal").then((m) => m.ExitScanModal),
  { ssr: false, loading: () => null }
)

interface ContentNewsletterModalProps {
  /**
   * Surface name threaded into the modal's analytics source (`<source>_modal`)
   * and subscribe context — e.g. "blog", "guide", "compare".
   */
  source: string
  /** Scroll-depth trigger; content pages default lower than tools' 0.85. */
  scrollThreshold?: number
  /**
   * Post category (blog) or grass/topic name (guide/compare) so the modal
   * pitches the reader's topic-specific care calendar instead of a generic
   * newsletter. Optional — omitting falls back to the generic calendar copy.
   */
  category?: string
  /** Post slug, for the scan modal's grass-named headings (blog only). */
  slug?: string
  /**
   * Server-computed isIdentifyAudience(slug, category). When true on a blog
   * post, the blog_exit_scan_modal_v1 "scan" arm swaps the newsletter body for
   * the exit-intent scan modal on the SAME trigger. Trigger, session cap, and
   * dismissal cooldown stay shared either way.
   */
  identifyIntent?: boolean
}

/**
 * Exit-intent / scroll-depth newsletter modal for content pages (blog posts,
 * guides, compare pages). Before this, the modal only existed on tool pages,
 * so ~81% of traffic (blog) could bounce without ever seeing an email ask —
 * the July 2026 PostHog audit measured only 12.7% of blog visitors ever saw
 * ANY capture surface. This is a passive fallback: it never fires for
 * authenticated users, dismissal starts a cooldown (see usage-tracker), and any successful
 * capture (including the inline widgets) suppresses it permanently.
 */
export function ContentNewsletterModal({
  source,
  scrollThreshold = 0.5,
  category,
  slug,
  identifyIntent,
}: ContentNewsletterModalProps) {
  const { showNewsletterModal, closeNewsletterModal } = useNewsletterPrompt({
    scrollThreshold,
    // On articles, scroll depth is an engagement signal, not an exit signal.
    // Reaching 50% only ARMS the trigger; it fires on scroll-up (leave
    // gesture) or read-complete (95%). Day-one blog data without this:
    // ~70% explicit dismissals from firing mid-read.
    requireScrollUpAfterDepth: true,
    // Second fire signal: an armed reader who stops scrolling for 4s has
    // paused, not left. Depth plus a pause is the two-signal read the popup
    // benchmarks favor over depth alone; a reader still moving never fires.
    idleAfterArmMs: 4000,
    // Timed trigger at the benchmark window for blog posts (Omnisend 45-60s,
    // Popupsmart 30-45s). Fires regardless of depth.
    timeThreshold: 45000,
    // Quiet period before any signal can fire. 6-10s beats immediate in the
    // large popup datasets; under ~5s is the interstitial-penalty zone.
    initialDelay: 6000,
  })

  // Experiment: suppress the passive modal on BLOG posts only, to read whether
  // removing the interruption lifts the surfaces that actually convert (inline
  // scan/diagnose CTAs). Fail-safe: any flag value other than "test" — including
  // undefined before flags resolve and off-prod where PostHog is disabled —
  // keeps the modal exactly as it is today. Guides and compare pages are never
  // suppressed. The hook above stays unconditional (rules-of-hooks).
  const modalOffVariant = useFeatureFlagVariantKey("blog_newsletter_modal_off_v1")
  const suppressed = modalOffVariant === "test" && source === "blog"

  // Experiment: on identify-intent blog posts, the "scan" arm swaps the
  // newsletter body for an exit-intent grass-ID scan modal on the same trigger
  // (blog_exit_scan_modal_v1). Fail-safe: any other value — undefined before
  // flags resolve, control, off-prod — renders the newsletter modal exactly as
  // today. off_v1 suppression keeps precedence over the swap so its in-flight
  // read stays clean. Hook stays unconditional (rules-of-hooks).
  const scanVariant = useFeatureFlagVariantKey("blog_exit_scan_modal_v1")
  const scanSwapNow =
    !suppressed && identifyIntent === true && source === "blog" && scanVariant === "scan"

  // Latch the body choice at the moment the trigger fires. Without this, a
  // flag resolving to "scan" AFTER the newsletter modal opened would swap the
  // body mid-open and double-fire impressions — one newsletter_modal_shown
  // plus one cta_surface_shown for a single trigger (Codex review 2026-08-26).
  // The latch resets when the modal closes so the next open re-decides.
  const scanSwapLatchRef = useRef<boolean | null>(null)
  if (showNewsletterModal && scanSwapLatchRef.current === null) {
    scanSwapLatchRef.current = scanSwapNow
  } else if (!showNewsletterModal && scanSwapLatchRef.current !== null) {
    scanSwapLatchRef.current = null
  }
  const scanSwap = showNewsletterModal ? scanSwapLatchRef.current === true : scanSwapNow

  // Denominator for the suppressed arm: without a "would have shown" event the
  // test arm has zero impressions and the two arms are not comparable. The
  // modal's own impression events fire inside its isOpen effect, so gating
  // isOpen zeroes newsletter_modal_shown for this arm by construction.
  // `scan_variant` rides additively so the two experiments stay separable
  // while both flags are live.
  const suppressionLoggedRef = useRef(false)
  useEffect(() => {
    if (!showNewsletterModal || !suppressed || suppressionLoggedRef.current) return
    suppressionLoggedRef.current = true
    trackEvent("newsletter_modal_suppressed", {
      source,
      experiment_variant: "test",
      // "unresolved" when the flag has not answered yet: stamping "control"
      // there would mislabel users whose assignment resolves to "scan" a beat
      // later (Codex review 2026-08-26).
      scan_variant:
        scanVariant === "scan" ? "scan" : scanVariant === undefined ? "unresolved" : "control",
    })
  }, [showNewsletterModal, suppressed, source, scanVariant])

  if (scanSwap) {
    return (
      <ExitScanModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        slug={slug}
        category={category}
      />
    )
  }

  return (
    <NewsletterSignupModal
      isOpen={showNewsletterModal && !suppressed}
      onClose={closeNewsletterModal}
      toolName={source}
      category={category}
      // Guide pages know the reader's grass — pass it so the care-plan email
      // is species-personalized instead of season-generic.
      grassType={source === "guide" ? category : undefined}
    />
  )
}
